import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getContactsAPI } from '../../../apis/contactAPIs';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';

import {
  createOrUpdateFamilyAPI,
  getFamilyByIdAPI,
  getFamilyRecommendationsAPI
} from '../../../apis/familyAPIs';
import { MembersType } from '../../../types';
import { getMemberIdsFromMembers } from './util/familyAdapter';

interface ISearchMembersResp {
  docs: MembersType[];
  limit: number;
  page: number;
  pages: number;
  total: number;
}

export interface ISearchMemberQuery {
  name: string;
  gender?: 'male' | 'female';
  state?: string;
  hasFamily?: boolean;
  sortValue?: string;
  pageNumber: number;
}

export enum RespState {
  IDLE,
  PENDING,
  SUCCEEDED,
  FAILED
}

interface IPaginationMeta {
  maxPageNumber: number | null;
  total: number;
}

export const searchMembers = createAsyncThunk(
  'familyTree/members',
  async (query: ISearchMemberQuery, { rejectWithValue }) => {
    try {
      const response = await getContactsAPI(
        query.name,
        query.pageNumber,
        {
          state: query.state,
          gender: query.gender,
          hasFamily: query.hasFamily
        },
        query.sortValue
      );
      if (response.error) {
        throw response.error;
      }
      return response;
    } catch (e) {
      return rejectWithValue('rejected');
    }
  }
);

export const getFamilyRecommendations = createAsyncThunk(
  'familyTree/recommendations',
  async (query: string[], { rejectWithValue }) => {
    try {
      const response = await getFamilyRecommendationsAPI(query);
      return response;
    } catch (e) {
      return rejectWithValue('rejected');
    }
  }
);

export const getFamilyById = createAsyncThunk(
  'familyTree/familyRelation',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await getFamilyByIdAPI(query);
      if (response.error) {
        throw response.error;
      }
      return response;
    } catch (e) {
      return rejectWithValue('rejected');
    }
  }
);

export const createOrUpdateFamily = createAsyncThunk(
  'familyTree/createOrUpdateFamily',
  async (familyRelation: FamilyRelation, { rejectWithValue }) => {
    try {
      const response = await createOrUpdateFamilyAPI(
        getMemberIdsFromMembers(familyRelation.members),
        familyRelation.id
      );
      if (response.error) {
        throw response.error;
      }
      return response;
    } catch (e) {
      return rejectWithValue('rejected');
    }
  }
);

export type FamilyMemberType = Pick<
  NonNullable<MembersType>,
  | '_id'
  | 'firstName'
  | 'lastName'
  | 'addressDetails'
  | 'profileImageUrl'
  | 'familyId'
  | 'contactDetails'
>;

export type FamilyRelation = {
  id: string | null;
  members: FamilyMemberType[];
  apiState: RespState;
  isDirty: boolean;
};

export type ICreateFamilyRelation = {
  familyId: string;
};

export type FamilyRelationResp = {
  familyId: string | null;
  members: FamilyMemberType[];
};

interface IFamilyTreeState {
  members: MembersType[];
  loading: RespState;
  getUserApiState: RespState;
  paginationMeta: IPaginationMeta;
  familyRelation: FamilyRelation;
  recommendations: FamilyMemberType[];
}

const initialState: IFamilyTreeState = {
  members: [],
  loading: RespState.IDLE,
  getUserApiState: RespState.IDLE,
  paginationMeta: {
    maxPageNumber: null,
    total: 0
  },
  familyRelation: {
    id: null,
    members: [],
    apiState: RespState.SUCCEEDED,
    isDirty: false
  },
  recommendations: []
};

// Then, handle actions in your reducers:
const familyTreeSlice = createSlice({
  name: 'familyTree',
  initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
    addToFamily: (state, { payload: member }) => {
      state.familyRelation.members.push(member);
      state.familyRelation.isDirty = true;
    },
    assignToExistingFamily: (state, { payload: memberId }) => {
      if (state.members?.length > 0) {
        const member: FamilyMemberType = state.recommendations.filter((m) => m._id === memberId)[0];
        state.familyRelation.members.push(member);
        state.familyRelation.isDirty = true;
      }
    },
    removeFromFamily: (state, { payload: memberId }) => {
      //TODO use familyRElation Unique ID here
      if (state.members?.length > 0) {
        const members = state.familyRelation.members.filter((m) => m._id !== memberId);
        state.familyRelation.members = members;
        state.familyRelation.isDirty = true;
      }
    },
    resetFamilyTreeContent: (state) => {
      //TODO use familyRElation Unique ID here
      state.familyRelation = initialState.familyRelation;
      state.recommendations = [];
    }
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(searchMembers.pending, (state) => {
      state.paginationMeta.maxPageNumber = null;
      state.getUserApiState = RespState.PENDING;
    });
    builder.addCase(searchMembers.fulfilled, (state, action: PayloadAction<ISearchMembersResp>) => {
      // Add user to the state array
      // when page is 1, we will replace the members
      // other we will append the value.
      if (action.payload.page == 1) {
        state.members = action.payload.docs;
      } else {
        action.payload.docs.forEach((member) => {
          state.members.push(member);
        });
      }
      state.paginationMeta.total = action.payload.total;
      state.paginationMeta.maxPageNumber = action.payload.pages;
      state.getUserApiState = RespState.SUCCEEDED;
    });
    builder.addCase(searchMembers.rejected, (state) => {
      state.paginationMeta.maxPageNumber = null;
      state.getUserApiState = RespState.FAILED;
      state.members = [];
      state.paginationMeta.total = 0;
    });
    builder.addCase(getFamilyById.pending, (state) => {
      state.paginationMeta.maxPageNumber = null;
      state.loading = RespState.PENDING;
    });
    builder.addCase(getFamilyById.fulfilled, (state, action: PayloadAction<FamilyRelationResp>) => {
      state.familyRelation = {
        id: action.payload.familyId,
        members: action.payload.members,
        isDirty: false,
        apiState: RespState.SUCCEEDED
      };
      state.recommendations = [];
      state.loading = RespState.SUCCEEDED;
    });
    builder.addCase(getFamilyById.rejected, (state) => {
      state.loading = RespState.FAILED;
      state.familyRelation = {
        id: '',
        members: [],
        isDirty: false,
        apiState: RespState.FAILED
      };
    });
    builder.addCase(
      createOrUpdateFamily.fulfilled,
      (state, action: PayloadAction<ICreateFamilyRelation>) => {
        state.familyRelation = {
          id: action.payload.familyId,
          members: state.familyRelation.members,
          isDirty: false,
          apiState: RespState.SUCCEEDED
        };
        state.loading = RespState.SUCCEEDED;
      }
    );
    builder.addCase(createOrUpdateFamily.rejected, (state) => {
      state.familyRelation.apiState = RespState.FAILED;
      state.familyRelation.isDirty = true;
    });
    builder.addCase(getFamilyRecommendations.pending, (state) => {
      state.loading = RespState.PENDING;
    });
    builder.addCase(getFamilyRecommendations.fulfilled, (state, action: PayloadAction<any>) => {
      let recommendations: FamilyMemberType[] = [];
      // @ts-ignore
      action.payload.map((member) => recommendations.push(member.recommendations));
      recommendations = uniqBy(flatten(recommendations), '_id');
      //remove existing members of family from recommendations
      recommendations = recommendations.filter((recommendedMember) => {
        return !state.familyRelation.members.some((member) => {
          return recommendedMember._id === member._id;
        });
      });
      state.recommendations = recommendations;
      state.loading = RespState.SUCCEEDED;
    });
    builder.addCase(getFamilyRecommendations.rejected, (state) => {
      state.loading = RespState.FAILED;
      state.recommendations = [];
    });
  }
});

// Action creators are generated for each case reducer function
// export const { increment, decrement, incrementByAmount } = familyTreeSlice.actions;
export const { addToFamily, assignToExistingFamily, resetFamilyTreeContent, removeFromFamily } =
  familyTreeSlice.actions;

export default familyTreeSlice.reducer;
